import { TransitionDownIcon, type IconComponent } from '@infinex/ui-icons';
import { cn } from '@infinex/ui-styles';

import type { SendToWalletIconType } from './getTargetWalletIcon';
import { getTargetWalletIcon } from './getTargetWalletIcon';
import type { TransactionAssetDisplayProps } from './TransactionAssetDisplay';
import { TransactionAssetDisplay } from './TransactionAssetDisplay';
import { TransactionGasDetailRow } from './TransactionGasDetailRow';
import type { TransactionProcessMessageOverrides } from './TransactionProcessMessage';
import { TransactionProcessMessage } from './TransactionProcessMessage';

import type { GasTopUpResult } from '../../hooks/gas/useGasTopUp';
import type { GasEstimate } from '../../types';
import type { CustodyIconType } from '../../utils';
import { getCustodyIcon } from '../../utils';
import { CustodyEmblemFromWalletId } from '../custody';
import type { ProcessingStatusCardProps } from '../transaction-details/ProcessingStatusCard';
import { ProcessingStatusCard } from '../transaction-details/ProcessingStatusCard';
import {
  TransactionDetailRow,
  TransactionDetailRowsSection,
} from '../transaction-details/TransactionDetailRow';

export type TransactionProcessingProps = {
  status: ProcessingStatusCardProps['status'];
  statusLabels: ProcessingStatusCardProps['statusLabels'];

  testId?: string | undefined;

  from?: {
    label: string | React.ReactNode;
    type: CustodyIconType;
    walletId?: string;
    title?: string;
  };
  to?: {
    label: string | React.ReactNode;
    type: SendToWalletIconType;
    walletId?: string;
    title?: string;
  };
  gas:
    | {
        gasEstimate: GasEstimate | null;
        gasTopUpResult: GasTopUpResult | null;
        isExternalSigner?: boolean;
      }
    | null
    | 'free';

  asset: TransactionAssetDisplayProps['asset'];
  symbol: string | null | undefined;
  amount: string;
  priceData: { usd: { rate: number } } | undefined;
  /** When set, render the primary asset as a Collector Crypt card. */
  card?: TransactionAssetDisplayProps['card'];

  toAssetDetails?: {
    asset: TransactionAssetDisplayProps['asset'];
    symbol: string | null | undefined;
    amount: string;
    priceData: { usd: { rate: number } } | undefined;
  };

  otherDetailRows?: React.ReactNode;
  /** Render the detail rows inside the status card instead of below it. */
  detailRowsInCard?: boolean;
  /** Extra classes for the status card's outer container. */
  cardClassName?: string;
  /** Extra classes for the status card's header text. */
  cardHeaderClassName?: string;
  /** Extra classes for the status card's content area. */
  cardContentClassName?: string;

  messageOverrides?: TransactionProcessMessageOverrides;
};

export function TransactionProcessing({
  status,
  statusLabels,
  testId,
  from,
  to,
  gas,
  asset,
  symbol,
  amount,
  priceData,
  card,
  toAssetDetails,
  otherDetailRows,
  detailRowsInCard = false,
  cardClassName,
  cardHeaderClassName,
  cardContentClassName,
  messageOverrides,
}: TransactionProcessingProps) {
  const detailRows = (
    <TransactionDetailRowsSection>
      {from && (
        <TransactionDetailRow
          label={from.title || 'From'}
          value={
            <WalletDisplay
              icon={getCustodyIcon(from.type)}
              walletId={from.walletId}
              label={from.label}
              hasError={status === 'error'}
            />
          }
        />
      )}

      {to && (
        <TransactionDetailRow
          label={to.title || 'To'}
          value={
            <WalletDisplay
              icon={getTargetWalletIcon(to.type)}
              walletId={to.walletId}
              label={to.label}
              hasError={status === 'error'}
              iconClassName={
                to.type === 'gas-account' ? 'fill-emphasis' : undefined
              }
            />
          }
        />
      )}

      {otherDetailRows}

      {gas && <TransactionGasDetailRow gas={gas} />}
    </TransactionDetailRowsSection>
  );

  return (
    <div className="flex flex-col space-y-4">
      <ProcessingStatusCard
        status={status}
        statusLabels={statusLabels}
        data-testid={testId}
        className={cn('rounded-3xl', cardClassName)}
        headerClassName={cardHeaderClassName}
        contentClassName={cn(
          'flex flex-col gap-[2px] p-0',
          {
            'bg-surfaceTwo': status === 'wait-for-approval',
            'bg-fillInfo': status === 'pending',
            'bg-fillPositive': status === 'success' || status === 'partial',
            'bg-fillCritical': status === 'error',
            'bg-surface': status === 'review',
          },
          cardContentClassName
        )}
      >
        <TransactionAssetDisplay
          asset={asset}
          symbol={symbol}
          amount={amount}
          priceData={priceData}
          card={card}
          className={
            detailRowsInCard
              ? 'rounded-xl rounded-t-2xl'
              : toAssetDetails
                ? 'rounded-b-xl'
                : 'rounded-t-xl'
          }
        />

        {toAssetDetails && (
          <>
            <div className="relative flex h-0 items-center justify-center">
              <div
                className="absolute flex size-[80px] items-center justify-center"
                style={{
                  background:
                    'radial-gradient(circle, #101114FF 0%, #101114FF 30%, transparent 70%)',
                }}
              >
                <TransitionDownIcon className="fill-secondary size-5" />
              </div>
            </div>

            <TransactionAssetDisplay
              asset={toAssetDetails.asset}
              symbol={toAssetDetails.symbol}
              amount={toAssetDetails.amount}
              priceData={toAssetDetails.priceData}
              className="rounded-t-xl"
            />
          </>
        )}

        {detailRowsInCard ? (
          <div className="bg-surfaceAlt w-full overflow-hidden rounded-xl rounded-b-2xl">
            {detailRows}
          </div>
        ) : null}
      </ProcessingStatusCard>

      {detailRowsInCard ? null : detailRows}

      <TransactionProcessMessage
        status={status}
        messageOverrides={messageOverrides}
      />
    </div>
  );
}

function WalletDisplay(props: {
  icon: IconComponent;
  walletId?: string;
  label: string | React.ReactNode;
  hasError: boolean;
  iconClassName?: string;
}) {
  const Icon = props.icon;
  return (
    <div className="flex items-center gap-x-1">
      {props.walletId && !props.hasError ? (
        <CustodyEmblemFromWalletId
          walletId={props.walletId}
          size="small"
          withoutBackground
        />
      ) : (
        <Icon
          className={cn(
            props.hasError ? 'fill-disabled' : 'fill-brand',
            props.iconClassName
          )}
          size="2xsmall"
        />
      )}
      <div
        className={cn(
          'flex flex-col gap-1 text-right text-sm font-medium text-wrap break-all',
          props.hasError ? 'text-disabled' : 'text-emphasis'
        )}
      >
        {props.label}
      </div>
    </div>
  );
}
